import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 15 — Programme/coach review and testimonial consent.
 * Mirrors commercialQueries.js's `request()` error contract exactly.
 * The optional video attach reuses the EXISTING `uploadFile()` orchestrator
 * from fileQueries.js (target_type="review") — this module never
 * reimplements upload/TUS logic, it only ever stores the resulting
 * `fas_id` pointer on the draft.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const rqk = {
  draft: (wrkId) => ["review-draft", wrkId],
  history: (wrkId) => ["review-history", wrkId],
  consent: (wrkId, rsbId) => ["review-consent", wrkId, rsbId],
  coachReviews: () => ["coach-reviews"],
};

export function useReviewDraft(workspaceId) {
  return useQuery({
    queryKey: rqk.draft(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/reviews/draft`),
  });
}

export function useSaveReviewDraft(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/reviews/draft`, { method: "PUT", body: JSON.stringify(payload) }),
    onSuccess: (data) => qc.setQueryData(rqk.draft(workspaceId), data),
  });
}

export function useSubmitReview(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/reviews/submit`, { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData(rqk.draft(workspaceId), { submission: null, answers: {}, consent: null });
      qc.invalidateQueries({ queryKey: rqk.history(workspaceId) });
      return data;
    },
  });
}

export function useReviewHistory(workspaceId) {
  return useQuery({
    queryKey: rqk.history(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/reviews`),
  });
}

export function useRecordConsent(workspaceId, rsbId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/reviews/${rsbId}/consent`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rqk.history(workspaceId) });
      qc.invalidateQueries({ queryKey: rqk.consent(workspaceId, rsbId) });
    },
  });
}

// -- Coach-only minimal surface ----------------------------------------------
export function useCoachReviews() {
  return useQuery({
    queryKey: rqk.coachReviews(),
    queryFn: () => request(`/api/rehearsal-room/coach/reviews`),
    retry: (failureCount, error) => error?.status ? error.status >= 500 && failureCount < 2 : failureCount < 2,
  });
}

export function useCompleteReviewTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ctkId) => jsonPost(`/api/rehearsal-room/coach/reviews/tasks/${ctkId}/complete`),
    onSuccess: () => qc.invalidateQueries({ queryKey: rqk.coachReviews() }),
  });
}

// Master v5 Prompt 19 — Reviews workbench: acknowledge/follow-up and
// consent-gated testimonial export. Follow-up never touches
// review_answers — it only ever creates a new, independent Message.
export function useFollowUpOnReview() {
  return useMutation({
    mutationFn: ({ rsbId, bodyText }) => jsonPost(`/api/rehearsal-room/coach/reviews/${rsbId}/follow-up`, { body_text: bodyText }),
  });
}

export function useTestimonialExport(rsbId) {
  return useQuery({
    queryKey: ["review-testimonial-export", rsbId],
    enabled: false,
    queryFn: () => request(`/api/rehearsal-room/coach/reviews/${rsbId}/testimonial-export`),
    retry: false,
  });
}
